import questionnaireTypesConfig from '../data/questionnaire-types.json';
import orientationConscienceData from '../data/orientation-conscience-questions.json';
import questionnaireData from '../assets/Questionnaire-des-dons-db.json';

/**
 * Get all available questionnaire types
 * @returns {Object} All questionnaire type configurations
 */
export const getQuestionnaireTypes = () => {
  return questionnaireTypesConfig;
};

/**
 * Get a specific questionnaire type configuration
 * @param {string} questionnaireId - The questionnaire ID
 * @returns {Object|null} The questionnaire type configuration or null if not found
 */
export const getQuestionnaireType = (questionnaireId) => {
  // First try to find by the questionnaire ID in the config values
  const typeConfig = Object.values(questionnaireTypesConfig).find(
    type => type.id === questionnaireId
  );
  
  if (typeConfig) {
    return typeConfig;
  }
  
  // Fallback: try to find by the config key (for backward compatibility)
  return questionnaireTypesConfig[questionnaireId] || null;
};

/**
 * Get active questionnaire types for display on landing page
 * @returns {Array} Array of active questionnaire type configurations
 */
export const getActiveQuestionnaireTypes = () => {
  return Object.values(questionnaireTypesConfig).filter(type => type.isActive);
};

/**
 * Get questions for a specific questionnaire type
 * @param {string} questionnaireId - The questionnaire ID (e.g., 'dons-spirituels-court')
 * @returns {Array} Array of questions for the questionnaire
 */
export const getQuestionsForQuestionnaire = (questionnaireId) => {
  // Find the questionnaire type
  const typeConfig = Object.values(questionnaireTypesConfig).find(
    type => type.id === questionnaireId
  );
  
  if (!typeConfig) {
    throw new Error(`Unknown questionnaire ID: ${questionnaireId}`);
  }

  switch (typeConfig.type) {
    case 'spiritual-gifts':
      return getSpiritualGiftsQuestions(typeConfig);
    case 'orientation-conscience':
      return getOrientationConscienceQuestions();
    default:
      throw new Error(`Unknown questionnaire type: ${typeConfig.type}`);
  }
};

/**
 * Get spiritual gifts questions based on configuration
 * @param {Object} typeConfig - The questionnaire type configuration
 * @returns {Array} Array of spiritual gifts questions
 */
const getSpiritualGiftsQuestions = (typeConfig) => {
  const questionsPerCategory = typeConfig.questionsPerCategory;
  const totalCategories = 21;
  const questions = [];

  for (let category = 1; category <= totalCategories; category++) {
    const categoryQuestions = questionnaireData.filter(q => q.categorie === category.toString());
    const selectedQuestions = categoryQuestions.slice(0, questionsPerCategory);
    questions.push(...selectedQuestions);
  }

  return questions;
};

/**
 * Get orientation conscience questions
 * @returns {Array} Array of orientation conscience questions
 */
const getOrientationConscienceQuestions = () => {
  return orientationConscienceData.questions.map(question => ({
    ...question,
    groups: Object.entries(orientationConscienceData.groups)
      .filter(([groupId, group]) => group.questionIds.includes(question.id))
      .map(([groupId]) => groupId)
  }));
};

/**
 * Calculate results for a questionnaire based on its type
 * @param {string} questionnaireId - The questionnaire ID
 * @param {Object} answers - The user's answers
 * @returns {Object} Calculated results
 */
export const calculateQuestionnaireResults = (questionnaireId, answers) => {
  const typeConfig = Object.values(questionnaireTypesConfig).find(
    type => type.id === questionnaireId
  );
  
  if (!typeConfig) {
    throw new Error(`Unknown questionnaire ID: ${questionnaireId}`);
  }

  switch (typeConfig.type) {
    case 'spiritual-gifts':
      return calculateSpiritualGiftsResults(answers, typeConfig);
    case 'orientation-conscience':
      return calculateOrientationConscienceResults(answers);
    default:
      throw new Error(`Unknown questionnaire type: ${typeConfig.type}`);
  }
};

/**
 * Calculate spiritual gifts results
 * @param {Object} answers - User answers
 * @param {Object} typeConfig - Questionnaire configuration
 * @returns {Object} Calculated results
 */
const calculateSpiritualGiftsResults = (answers, typeConfig) => {
  const totalCategories = 21;
  const results = [];

  // Categories mapping
  const categories = [
    "Administration", "Apostolat", "Relation d'aide", "Artisanat", "Arts créatifs",
    "Discernement", "Encouragement", "Évangélisation", "Foi", "Libéralité",
    "Guérison", "Service", "Hospitalité", "Connaissance", "Leadership",
    "Compassion", "Miracles", "Prophétie", "Berger/Pasteur", "Enseignement", "Sagesse"
  ];

  // Get the questions for this questionnaire type to establish the mapping
  const questions = getSpiritualGiftsQuestions(typeConfig);

  for (let category = 1; category <= totalCategories; category++) {
    let total = 0;
    let count = 0;

    Object.keys(answers).forEach(questionIndex => {
      const questionArrayIndex = parseInt(questionIndex);
      const question = questions[questionArrayIndex];
      
      if (question && parseInt(question.categorie) === category) {
        total += parseInt(answers[questionIndex]);
        count++;
      }
    });

    if (count > 0) {
      results.push({
        category: category,
        name: categories[category - 1],
        score: total / count,
        total: total,
        count: count
      });
    }
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return {
    scores: results,
    totalQuestions: Object.keys(answers).length,
    answers: answers,
    displayType: 'radar'
  };
};

/**
 * Calculate orientation conscience results
 * @param {Object} answers - User answers
 * @returns {Object} Calculated results
 */
const calculateOrientationConscienceResults = (answers) => {
  const groups = orientationConscienceData.groups;
  const groupAverages = {};
  const results = [];

  // Calculate averages for each group
  Object.entries(groups).forEach(([groupId, group]) => {
    let sum = 0;
    let count = 0;

    group.questionIds.forEach(questionId => {
      const answer = answers[questionId];
      if (answer !== null && answer !== undefined) {
        sum += parseInt(answer);
        count++;
      }
    });

    const average = count > 0 ? sum / count : 0;
    groupAverages[groupId] = average;
    
    results.push({
      groupId: groupId,
      name: group.name,
      average: average,
      questionCount: count
    });
  });

  // Prepare scatter plot data
  const scatterData = orientationConscienceData.scatterPlotPairs.map(pair => ({
    id: String(pair.id),
    title: pair.title,
    xGroup: pair.xGroup,
    yGroup: pair.yGroup,
    xLabel: groups[pair.xGroup].name,
    yLabel: groups[pair.yGroup].name,
    xValue: groupAverages[pair.xGroup],
    yValue: groupAverages[pair.yGroup]
  }));

  return {
    groupAverages: groupAverages,
    results: results,
    scatterData: scatterData,
    totalQuestions: Object.keys(answers).length,
    answers: answers,
    displayType: 'scatter'
  };
};

/**
 * Get questionnaire type by questionnaire ID
 * @param {string} questionnaireId - The questionnaire ID
 * @returns {string} The questionnaire type
 */
export const getQuestionnaireTypeByID = (questionnaireId) => {
  const typeConfig = Object.values(questionnaireTypesConfig).find(
    type => type.id === questionnaireId
  );
  return typeConfig ? typeConfig.type : null;
};